/**
 * Button List
 * List of button elements that make up a button list array
 */
import { Button, ButtonGroup as MuiButtonGroup } from '@mui/material';
import React, { memo } from 'react';

import { useIsMobile } from '@hooks/useIsMobile';

interface ButtonGroupProps {
  /**
   * Array of each button's selected state
   */
  selected: boolean[];
  /**
   * Callback function for when the button is clicked
   */
  onClick: ((value: number | string) => void) | null;
  /**
   * Values of each button in the list
   */
  buttons: { name: string; value: number | string }[];
}

const ButtonGroup: React.FC<ButtonGroupProps> = ({
  selected = [],
  onClick = null,
  buttons = [],
}) => {
  const isMobile = useIsMobile();

  /**
   * Callback function for when a button is clicked
   * @param value Numeric value of the clicked button
   */
  const handleClick = (value: number | string) => {
    onClick?.(value);
  };

  return (
    <MuiButtonGroup
      orientation="horizontal"
      size={isMobile ? 'small' : 'medium'}
      sx={{
        '& > .MuiButton-root': {
          border: 'none',
          borderRadius: '17px',
          textTransform: 'capitalize',
          boxShadow: 'none',
          '&:hover': {
            border: 'none',
            borderRadius: '17px',
          },
        },
      }}
      variant={undefined}
    >
      {buttons.map(({ name, value }, index) => {
        const isSelected = selected[index];
        return (
          <Button
            key={`button-${index}`}
            onClick={() => handleClick(value)}
            color="primary"
            size={isMobile ? 'small' : 'medium'}
            sx={{
              backgroundColor: (theme) =>
                isSelected ? theme.palette.primary.main : 'transparent',
              color: (theme) =>
                isSelected
                  ? theme.palette.primary.contrastText
                  : theme.palette.text.primary,
              '&:hover': {
                backgroundColor: (theme) =>
                  isSelected ? theme.palette.primary.main : undefined,
              },
            }}
          >
            {name}
          </Button>
        );
      })}
    </MuiButtonGroup>
  );
};

export default memo(ButtonGroup);
